import React, { useEffect, useState } from "react";
import "./mobileAddClient.css";
import api from "../../../api";

// ─── helpers ────────────────────────────────────────────────────────────────

const EMPTY_ROW = () => ({
  _id:            Math.random().toString(36).slice(2),
  partyEstRateId: "",
  productId:      "",
  qty:            "",
  rate:           "",
});

const buildEstString = (rows) =>
  rows
    .filter((r) => r.productId || r.qty || r.rate)
    .map((r) => `${r.partyEstRateId}!${r.productId}!${r.qty}!${r.rate}!$`)
    .join("");

const TOP_FIELDS = [
  { key: "name",                  label: "Client Name"         },
  { key: "phone",                 label: "Mobile"              },
  { key: "contactPerson",         label: "Contact Person"      },
  { key: "email",                 label: "Email"               },
  { key: "paymentTerms",          label: "Payment Terms"       },
  { key: "currentSupplier",       label: "Current Supplier"    },
  { key: "buyingFrequency",       label: "Buying Frequency"    },
  { key: "estMonthlyConsumption", label: "Monthly Consumption (₹)" },
];

// ─── component ──────────────────────────────────────────────────────────────

export const MobileAddClient = ({ onClose, mode = "add", partyId = null }) => {
  const salesPersonId = localStorage.getItem("salesPersonId");
  const [productList,    setProductList]    = useState([]);
  const [form,           setForm]           = useState({
    name: "", contactPerson: "", phone: "", email: "",
    paymentTerms: "", currentSupplier: "", buyingFrequency: "",
    estMonthlyConsumption: "",
  });
  const [rows,           setRows]           = useState([EMPTY_ROW()]);
  const [savedPartyId,   setSavedPartyId]   = useState(null);
  const [savedOnce,      setSavedOnce]      = useState(false);
  const [isLocked,       setIsLocked]       = useState(false);
  const [checkedIds,     setCheckedIds]     = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [sendingQuote,   setSendingQuote]   = useState(false);
  const [saveMsg,        setSaveMsg]        = useState("");
  const [quoteMsg,       setQuoteMsg]       = useState("");
  const [errors,         setErrors]         = useState({});

  useEffect(() => {
    api.get("/api/productlist")
      .then((r) => setProductList(r.data?.data || []))
      .catch((e) => console.error("Product list error:", e));
  }, []);

  useEffect(() => {
    if (mode !== "update" || !partyId) return;
    api.get("/api/partydetail", { params: { PartyId: partyId } })
      .then((r) => {
        const d = r.data?.data;
        if (!d) return;
        const p = d.party?.[0] || {};
        setForm({
          name:                   p.PartyName                  || "",
          contactPerson:          p.ConPersonName              || "",
          phone:                  p.ConMobile                  || "",
          email:                  p.ConEmail                   || "",
          paymentTerms:           p.ParPaymentTerms            || "",
          currentSupplier:        p.ParCurrentSupplier         || "",
          buyingFrequency:        p.ParEstBuyingFrequncy       || "",
          estMonthlyConsumption:  p.ParEstMonthlyConsumption   || "",
        });
        const estRows = (d.estRates || []).map((er) => ({
          _id:            Math.random().toString(36).slice(2),
          partyEstRateId: er.PartyEstRateId     ?? "",
          productId:      er.ParEstProductId    ?? "",
          qty:            er.ParEstMonthlyRqQty ?? "",
          rate:           er.ParEstRate         ?? "",
        }));
        if (estRows.length > 0) setRows(estRows);
        setSavedPartyId(partyId);
        setSavedOnce(true);
        setIsLocked(true);
      })
      .catch((e) => console.error("Party detail error:", e));
  }, [mode, partyId]);

  // ─── field helpers ──────────────────────────────────────────────────────
  const setFormField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const setRowField = (id, key, val) => {
    setRows((prev) => prev.map((r) => r._id === id ? { ...r, [key]: val } : r));
  };

  const addRow  = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r._id !== id));

  // ─── validation ─────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.name?.toString().trim()) errs.name = true;
    const needsContact = !!form.phone?.toString().trim() || !!form.email?.toString().trim();
    if (needsContact && !form.contactPerson?.toString().trim()) errs.contactPerson = true;

    // Only validate rows that have at least one field filled (partial rows are invalid)
    rows.forEach((r) => {
      const hasAny = r.productId || r.qty || r.rate;
      if (hasAny) {
        if (!r.productId) errs[`prod_${r._id}`] = true;
        if (!r.qty)       errs[`qty_${r._id}`]  = true;
        if (!r.rate)      errs[`rate_${r._id}`] = true;
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── parse response synchronously ───────────────────────────────────────
  const parseResponse = (data) => {
    const p = data.party?.[0] || {};
    const newForm = {
      name:                   p.PartyName                  || "",
      contactPerson:          p.ConPersonName              || "",
      phone:                  p.ConMobile                  || "",
      email:                  p.ConEmail                   || "",
      paymentTerms:           p.ParPaymentTerms            || "",
      currentSupplier:        p.ParCurrentSupplier         || "",
      buyingFrequency:        p.ParEstBuyingFrequncy       || "",
      estMonthlyConsumption:  p.ParEstMonthlyConsumption   || "",
    };
    const newRows = (data.estRates || []).map((er) => ({
      _id:            Math.random().toString(36).slice(2),
      partyEstRateId: er.PartyEstRateId     ?? "",
      productId:      er.ParEstProductId    ?? "",
      qty:            er.ParEstMonthlyRqQty ?? "",
      rate:           er.ParEstRate         ?? "",
    }));
    const newPartyId = p.PartyId || null;
    return { newForm, newRows, newPartyId };
  };

  // ─── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) {
      setSaveMsg("Please fill all required fields.");
      setTimeout(() => setSaveMsg(""), 3000);
      return;
    }
    setSaving(true);
    setSaveMsg("");
    try {
      const isUpdate = mode === "update" || !!savedPartyId;
      const payload = {
        SaveType:               isUpdate ? "U" : "I",
        ...(isUpdate && { PartyId: savedPartyId ?? partyId }),
        Name:                   form.name,
        ContactPerson:          form.contactPerson,
        Phone:                  form.phone,
        Email:                  form.email,
        SalesPersonId:          salesPersonId,
        PaymentTerms:           form.paymentTerms,
        CurrentSupplier:        form.currentSupplier,
        BuyingFrequency:        form.buyingFrequency,
        EstMonthlyConsumption:  form.estMonthlyConsumption,
        UserCode:               "ADMIN",
        EstProductRateString:   buildEstString(rows),
      };

      const res  = await api.post("/api/partyestratedtladdupdate", payload);
      const data = res.data?.data;

      if (data) {
        const { newForm, newRows, newPartyId } = parseResponse(data);
        setForm(newForm);
        setRows(newRows.length > 0 ? newRows : [EMPTY_ROW()]);
        if (newPartyId) setSavedPartyId(newPartyId);
      }

      setSavedOnce(true);
      setIsLocked(true);
      setCheckedIds([]);
      setSaveMsg("Saved successfully!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      console.error("Save error:", e);
      setSaveMsg("Save failed. Please try again.");
      setTimeout(() => setSaveMsg(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ─── edit ────────────────────────────────────────────────────────────────
  const handleEdit = () => {
    setIsLocked(false);
    setCheckedIds([]);
  };

  // ─── send quote ──────────────────────────────────────────────────────────
  const canSendQuote = savedOnce && isLocked && checkedIds.length > 0;

  const handleSendQuote = async () => {
    if (!canSendQuote) return;
    setSendingQuote(true);
    setQuoteMsg("");
    try {
      await api.post("/api/emailquote", { partyestimaterateid: checkedIds.join(",") });
      setQuoteMsg("Quote sent successfully!");
      setTimeout(() => setQuoteMsg(""), 3000);
    } catch (e) {
      console.error("Send quote error:", e);
      setQuoteMsg("Failed to send quote.");
      setTimeout(() => setQuoteMsg(""), 3000);
    } finally {
      setSendingQuote(false);
    }
  };

  // ─── checkbox toggle ────────────────────────────────────────────────────
  const toggleCheck = (partyEstRateId) => {
    const id = String(partyEstRateId);
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ─── render ─────────────────────────────────────────────────────────────
  const contactPersonLabel =
    form.phone?.trim() || form.email?.trim() ? "Contact Person *" : "Contact Person";

  return (
    <div className="mobileAddClientOverlay">
      <div className="mobileAddClientMain">

        {/* Header */}
        <div className="mobileAddClientHeader">
          <p className="mobileAddClientHeaderTitle">
            {mode === "update" ? "Update Client" : "Add Client"}
          </p>
          <span className="material-symbols-outlined mobileAddClientCloseIcon" onClick={onClose}>
            close
          </span>
        </div>

        {/* Scrollable content */}
        <div className="mobileAddClientBody">

          {/* Info fields */}
          <div className="mobileAddClientInfoSection">
            {TOP_FIELDS.map(({ key, label }) => {
              const displayLabel =
                key === "contactPerson" ? contactPersonLabel :
                key === "name"          ? `${label} *` :
                label;
              return (
                <div key={key} className="mobileAddClientFieldRow">
                  <p className={`mobileAddClientFieldLabel${errors[key] ? " mobileAddClientLabelError" : ""}`}>
                    {displayLabel}
                  </p>
                  <input
                    className={`mobileAddClientFieldInput${errors[key] ? " mobileAddClientInputError" : ""}`}
                    value={form[key]}
                    disabled={isLocked}
                    onChange={(e) => setFormField(key, e.target.value)}
                    placeholder={`Enter ${label.toLowerCase()}...`}
                  />
                </div>
              );
            })}
          </div>

          {/* Product mix header */}
          <div className="mobileAddClientProductHeader">
            <p className="mobileAddClientProductTitle">Current product mix</p>
            {!isLocked && (
              <button className="mobileAddClientAddRowBtn" onClick={addRow}>
                <span className="material-symbols-outlined">add</span>
                Add Row
              </button>
            )}
          </div>

          {/* Product rows */}
          <div className="mobileAddClientProductList">
            {rows.map((row, idx) => {
              const canCheck  = isLocked && !!row.partyEstRateId;
              const isChecked = checkedIds.includes(String(row.partyEstRateId));
              return (
                <div key={row._id} className="mobileAddClientProductRow">
                  <div className="mobileAddClientProductRowTop">
                    <div className="mobileAddClientProductRowLeft">
                      <input
                        type="checkbox"
                        className="mobileAddClientCheckbox"
                        checked={isChecked}
                        disabled={!canCheck}
                        onChange={() => toggleCheck(row.partyEstRateId)}
                      />
                      <p className="mobileAddClientRowNum">Row {idx + 1}</p>
                    </div>
                    {!isLocked && rows.length > 1 && (
                      <span
                        className="material-symbols-outlined mobileAddClientRemoveRow"
                        onClick={() => removeRow(row._id)}
                      >
                        remove_circle_outline
                      </span>
                    )}
                  </div>

                  <div className="mobileAddClientFieldRow">
                    <p className={`mobileAddClientFieldLabel${errors[`prod_${row._id}`] ? " mobileAddClientLabelError" : ""}`}>
                      Product
                    </p>
                    <div className="mobileAddClientSelectWrapper">
                      <select
                        className={`mobileAddClientProductSelect${errors[`prod_${row._id}`] ? " mobileAddClientSelectError" : ""}`}
                        value={row.productId}
                        disabled={isLocked}
                        onChange={(e) => setRowField(row._id, "productId", e.target.value)}
                      >
                        <option value="">Select product...</option>
                        {productList.map((p) => (
                          <option key={p.ProductId} value={p.ProductId}>
                            {p.ProductName}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined mobileAddClientSelectArrow">expand_more</span>
                    </div>
                  </div>

                  <div className="mobileAddClientQtyRateRow">
                    <div className="mobileAddClientFieldGroup">
                      <p className={`mobileAddClientFieldLabel${errors[`qty_${row._id}`] ? " mobileAddClientLabelError" : ""}`}>
                        Qty
                      </p>
                      <input
                        className={`mobileAddClientFieldInput${errors[`qty_${row._id}`] ? " mobileAddClientInputError" : ""}`}
                        type="number" min="0" placeholder="Qty"
                        value={row.qty}
                        disabled={isLocked}
                        onChange={(e) => setRowField(row._id, "qty", e.target.value)}
                      />
                    </div>
                    <div className="mobileAddClientFieldGroup">
                      <p className={`mobileAddClientFieldLabel${errors[`rate_${row._id}`] ? " mobileAddClientLabelError" : ""}`}>
                        Rate
                      </p>
                      <input
                        className={`mobileAddClientFieldInput${errors[`rate_${row._id}`] ? " mobileAddClientInputError" : ""}`}
                        type="number" min="0" placeholder="Rate"
                        value={row.rate}
                        disabled={isLocked}
                        onChange={(e) => setRowField(row._id, "rate", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* Footer */}
        <div className="mobileAddClientFooter">
          {(saveMsg || quoteMsg) && (
            <div className="mobileAddClientMsgArea">
              {saveMsg && (
                <p className={`mobileAddClientMsg ${saveMsg.includes("success") ? "mobileAddClientMsgSuccess" : "mobileAddClientMsgError"}`}>
                  {saveMsg}
                </p>
              )}
              {quoteMsg && (
                <p className={`mobileAddClientMsg ${quoteMsg.includes("success") ? "mobileAddClientMsgSuccess" : "mobileAddClientMsgError"}`}>
                  {quoteMsg}
                </p>
              )}
            </div>
          )}

          {!savedOnce && (
            <p className="mobileAddClientQuoteHint">Save first to enable Send Quote</p>
          )}
          {savedOnce && !isLocked && (
            <p className="mobileAddClientQuoteHint">Save changes to enable quote</p>
          )}
          {savedOnce && isLocked && checkedIds.length === 0 && (
            <p className="mobileAddClientQuoteHint">Select rows to send quote</p>
          )}

          <div className="mobileAddClientFooterBtns">
            <p
              className={`mobileAddClientSendQuoteText${canSendQuote ? "" : " mobileAddClientSendQuoteDisabled"}`}
              onClick={handleSendQuote}
            >
              {sendingQuote ? "Sending..." : "Send Quote"}
            </p>

            {savedOnce && isLocked ? (
              <p className="mobileAddClientEditText" onClick={handleEdit}>Edit</p>
            ) : (
              <p
                className={`mobileAddClientSaveText${saving ? " mobileAddClientSaveDisabled" : ""}`}
                onClick={!saving ? handleSave : undefined}
              >
                {saving ? "Saving..." : "Save & Exit"}
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};